import React, { useMemo, useState } from 'react';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  LinkIcon,
} from '@revfluence/fresh-icons/regular/esm';
import 'react-quill/dist/quill.snow.css';
import { v4 as uuidv4 } from 'uuid';
import styles from './RichTextEditor.scss';

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  excludeModules?: string[];
}

interface CustomToolbarProps {
  id: string;
  excludeModules?: string[];
}

const CustomToolbar: React.FC<CustomToolbarProps> = ({ id, excludeModules }) => (
  <div id={id} className="ql-toolbar flex items-center p-1 space-x-1">
    <span className="ql-formats">
      <button className="ql-bold p-1.5 hover:bg-grey-1 rounded" aria-label="Bold">
        <BoldIcon width={16} height={16} />
      </button>
      <button className="ql-italic p-1.5 hover:bg-grey-1 rounded" aria-label="Italic">
        <ItalicIcon width={16} height={16} />
      </button>
      <button className="ql-underline p-1.5 hover:bg-grey-1 rounded" aria-label="Underline">
        <UnderlineIcon width={16} height={16} />
      </button>
    </span>
    <div className="h-5 w-px bg-border mx-1" />
    <span className="ql-formats">
      <button className="ql-align p-1.5 hover:bg-grey-1 rounded" value="" aria-label="Align Left">
        <AlignLeftIcon width={16} height={16} />
      </button>
      <button className="ql-align p-1.5 hover:bg-grey-1 rounded" value="center" aria-label="Align Center">
        <AlignCenterIcon width={16} height={16} />
      </button>
      <button className="ql-align p-1.5 hover:bg-grey-1 rounded" value="right" aria-label="Align Right">
        <AlignRightIcon width={16} height={16} />
      </button>
    </span>
    {
      !excludeModules?.includes('link') && (
        <>
          <div className="h-5 w-px bg-border mx-1" />
          <span className="ql-formats">
            <button className="ql-link p-1.5 hover:bg-grey-1 rounded" aria-label="Link">
              <LinkIcon width={16} height={16} />
            </button>
          </span>
        </>
      )
    }
  </div>
);

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Write something...',
  className,
  excludeModules = [],
}) => {
  const [content, setContent] = useState(value);

  const toolbarId = useMemo(() => `custom-editor-toolbar-${uuidv4()}`, []);

  const ReactQuill = useMemo(() => (
    (typeof window === 'object' && typeof document === 'object')
      ? require('react-quill')
      : () => null
  ), []);

  const handleChange = (newContent: string) => {
    setContent(newContent);
    onChange?.(newContent);
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: `#${toolbarId}`,
    },
  }), [toolbarId]);

  const formats = useMemo(() => [
    'bold',
    'italic',
    'underline',
    'align',
    'link',
  ], []);

  return (
    <div className={`${styles.RichTextEditor} ${className || ''}`}>
      <CustomToolbar id={toolbarId} excludeModules={excludeModules} />
      <ReactQuill
        theme="snow"
        value={content}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
      />
    </div>
  );
};

export default RichTextEditor;
